/**
 * Mutation Dependencies
 */
import Vue from 'vue'
import { getState } from './state'
import { get, isEmpty, cloneDeep } from 'lodash'

/**
 * Handle a user selected option on the project choice wizard
 *
 * @param  {Object} state
 * @return {Void}
 */
const PROJECT_MAKE_SELECTION = (state, params) => {
    if (params.target === 'primary') {
        state.projectChoices.selected.secondary = null
    }
    let context = state.projectChoices.selected[params.target]
    state.projectChoices.selected[params.target] = context === params.value
        ? null
        : params.value
}

/**
 * Update the form tab
 *
 * @param  {Object} state
 * @param  {String} tab
 * @return {Void}
 */
const CHANGE_FORM_TAB = (state, tab) => {
    state.activeTab = tab
}

/**
 * Reset the project choice wizard to its default state
 *
 * @param  {Object} state
 * @return {Void}
 */
const PROJECT_CHOICES_RESET = (state) => {
    state.projectChoices.selected.secondary = null
    state.projectChoices.selected.primary = null
}

/**
 * Update a value on the form state object
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_PROJECT_VALUE = (state, payload) => {
    let target = get(state.data, payload.base, false)
    if (target) {
        if (!state.dirty) {
            state.dirty = true
        }
        target[payload.target] = payload.value
    }
}

/**
 * Handle failed entry
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_PROJECT_VALUE_FAILED = (state, payload) => {
    console.log(payload)
}

/**
 * Set the project loading state
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_PROJECT_FORM = (state) => {
    state.pending = true
}

/**
 * Push a new debt object to the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const ADD_NEW_DEBT = (state, payload) => {
    state.data.debt.debt.push(payload)
}

/**
 * Update the state of an existing debt record
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_EXISTING_DEBT = (state, payload) => {
    state.data.debt.debt.splice(payload.index, 1, payload.value)
}

/**
 * Successful project form retrieval
 *
 * @param  {Object} state
 * @return {Void}
 */
const SET_PROJECT_FORM = (state, payload) => {
    Vue.set(state, 'options', payload.options)
    Vue.set(state, 'data', payload.model)
    Vue.set(state, 'tabs', payload.tabs)
    if (!state.data.meta) {
        Vue.set(state.data, 'meta', {
            rentroll_file_id: null,
            pnl_file_id: null
        })
    }
    state.pending = false
    state.loaded = true
}

/**
 * Failed form retrieval
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_PROJECT_FORM_FAILURE = (state) => {
    state.pending = false
    state.loaded = false
}

/**
 * Reset the form
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_FORM = (state) => {
    state.data = {}
    state.errors = {}
    state.options = {}
    state.tabs = []
    state.activeTab = 'project'
    state.dirty = false
    state.pending = false
    state.success = false
    state.loaded = false
}

/**
 * Push a new object to a repeatable form array
 *
 * @param  {Object} payload
 * @param  {Object} state
 * @return {Void}
 */
const ADD_REPEATABLE_PROJECT_VALUE = (state, obj) => {
    let base = get(state.data, obj.base, false)
    if (base) {
        if (!state.dirty) { state.dirty = true }
        if (obj.insertAfter !== undefined) {
            base[obj.target].splice(obj.insertAfter + 1, 0, obj.payload)
        } else {
            base[obj.target].push(obj.payload)
        }
    }
}

/**
 * TODO
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_REPEATABLE_PROJECT_VALUE = (state, payload) => {}

/**
 * Remove a repeater object
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const REMOVE_REPEATABLE_PROJECT_VALUE = (state, payload) => {
    let target = get(state.data, payload.base, false)
    if (target) {
        target.splice(payload.index, 1)
    }
}

/**
 * Modify the position of a repeater object in the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_REPEATABLE_PROJECT_VALUE_POSITION = (state, payload) => {
    let target = get(state.data, payload.base, false)
    if (target) {
        target.splice(payload.newIndex, 0, target.splice(payload.oldIndex, 1)[0])
    }
}

/**
 * Update an error's array index value to match the new index
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_ERROR_INDEX = (state, payload) => {
    let target = get(state.errors, payload.base, false)
    if (target) {
        target[payload.currentIndex].index = payload.newIndex
    }
}

/**
 * Updte an existing CAPEX state object
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_EXISTING_CAPEX = (state, payload) => {
    let target = get(state.data.capex, payload.base, false)
    if (target) {
        target.splice(payload.index, 1, payload.value)
    }
}

/**
 * Update the state of a repeatable object value
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_REPEATABLE_OBJECT_VALUE = (state, payload) => {
    let target = get(state.data, payload.base, false)
    if (target) {
        target[payload.target] = payload.value
    }
}

/**
 * Replace an entire repeater object with a new one
 *
 * @param  {Object} state
 * @return {Void}
 */
const REPLACE_REPEATER_OBJECT = (state, payload) => {
    let target = get(state.data, payload.base, false)
    if (target) {
        target.splice(payload.index, 1, payload.value)
    }
}

/**
 * Add a new equity object to the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const ADD_NEW_EQUITY = (state, payload) => {
    state.data.equity[payload.key].push(payload.data)
}

/**
 * Update an existing equity object in the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_EXISTING_EQUITY = (state, payload) => {
    state.data.equity[payload.key].splice(payload.index, 1, payload.value)
}

/**
 * Remove an existing equity object from the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {object}
 */
const REMOVE_EQUITY = (state, payload) => {
    state.data.equity[payload.key].splice(payload.index, 1)
}

/**
 * Set the pending state of the project form
 *
 * @param  {Object} state
 * @return {Void}
 */
const PROJECT_SAVE = (state) => {
    state.attemptingSave = true
}

/**
 * Set the success parameters on the state object.
 *
 * @param  {Object} state
 * @return {Void}
 */
const PROJECT_SAVE_SUCCESS = (state) => {
    state.success = true
    state.attemptingSave = false
}

/**
 * Reset the success params
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_SAVE_SUCCESS = (state) => {
    state.success = false
    state.attemptingSave = false
}

/**
 * Handle a failed POST request and assign the state errors
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const PROJECT_SAVE_FAILURE = (state, errors) => {
    state.errors = errors
    state.attemptingSave = false
}

/**
 * Reset the state
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_STATE = (state) => {
    let originalState = getState()
    for (let prop in originalState) {
        Vue.set(state, prop, originalState[prop])
    }
}

/**
 * Remove an error key in the errors object state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const REMOVE_ERROR_KEY = (state, payload) => {
    let base = get(state.errors, payload.base, null)

    if (Number.isInteger(payload.index) && payload.type !== undefined) {
        let idx = state.errors[payload.base][payload.type]
            .findIndex(error => error.index === payload.index)

        if (idx > -1) {
            Vue.delete(state.errors[payload.base][payload.type][idx], payload.key)
            let objectKeys = Object.keys(state.errors[payload.base][payload.type][idx])
            if (objectKeys.length === 1 && objectKeys[0] === 'index') {
                state.errors[payload.base][payload.type].splice(idx, 1)
            }
        }

        if (state.errors[payload.base][payload.type].length === 0) {
            Vue.delete(state.errors[payload.base], payload.type)
        }
    } else {
        Vue.delete(base, payload.key)
    }

    if (isEmpty(base)) {
        let split = payload.base.split('.')
        if (split.length > 1) {
            for (var i = split.length - 1; i >= 0; i--) {
                if (i > 0) {
                    if (isEmpty(state.errors[split[0]][split[i]])) {
                        Vue.delete(state.errors[split[0]], split[i])
                    }
                }
            }
            if (isEmpty(state.errors[split[0]])) {
                Vue.delete(state.errors, split[0])
            }
        } else {
            Vue.delete(state.errors, payload.base)
        }
    }
}

/**
 * Remove a non-relative object from a relative capex item when the non-relative item has been removed
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const REMOVE_DELETED_RELATIVE_VALUE = (state, payload) => {
    let target = get(state, payload.path)
    if (target) {
        target.splice(payload.index, 1)
    }
}

/**
 * Remove an error in the capex
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const REMOVE_CAPEX_ERROR = (state, payload) => {
    state.errors.capex[payload.type].splice(payload.index, 1)
    if (state.errors.capex[payload.type].length === 0) {
        Vue.delete(state.errors.capex, payload.type)
    }
    if (isEmpty(state.errors.capex)) {
        Vue.delete(state.errors, 'capex')
    }
}

/**
 * Insert the cloned capex into the capex array
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const ADD_CAPEX_CLONE = (state, payload) => {
    state.data.capex[payload.type].splice(payload.index, 0, payload.clone)
}

/**
 * Update a capex value
 *
 * @param  {Object} state
 * @return {Void}
 */
const UPDATE_RELATIVE_VALUE = (state, payload) => {
    let relativeItems = get(state.data, payload.base)
    relativeItems[payload.index].amount = payload.value
}

/**
 * Apply a fresh working capital model to the appropriate serializer key
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const SET_WORKING_CAPITAL_MODEL = (state, payload) => {
    Vue.set(state.data.capex, 'WorkingCapitalSerializer', payload)
}

/**
 * Nullify the working capital model key
 *
 * @param  {Object} state
 * @return {Void}
 */
const REMOVE_WORKING_CAPITAL_MODEL = (state) => {
    state.data.capex['WorkingCapitalSerializer'] = null
}

/**
 * Update the base debt object
 *
 * @param  {Object} state
 * @return {Void}
 */
const SET_DEBT_OBJECT = (state, payload) => {
    state.data.debt = payload
}

/**
 * Restore a draft state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const GET_DRAFT_SUCCESS = (state, payload) => {
    state.draft = payload
}

/**
 * Reset the draft state
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_DRAFT_FAILURE = (state) => {
    state.draft = {}
}

/**
 * Restore a project to the draft state
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESTORE_DRAFT = (state) => {
    if (!isEmpty(state.draft)) {
        Vue.set(state, 'data', cloneDeep(state.draft))
    }
}

/**
 * Set the edit state from new data
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const SET_EDIT_PAYLOAD = (state, payload) => {
    Vue.set(state, 'data', cloneDeep(payload.data))
    Vue.set(state, 'meta', cloneDeep(payload.meta))
}

/**
 * Set the draft state
 *
 * @param  {Object} state
 * @return {Void}
 */
const SAVING_DRAFT = (state) => {
    state.saving_draft = true
}

/**
 * Reset the draft state
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_SAVING_DRAFT = (state) => {
    state.saving_draft = false
}

/**
 * Set the unit values on the state's form object
 *
 * @param  {Object} state
 * @param  {Object} data
 * @return {Void}
 */
const IMPORT_UNIT_VALUES = (state, data) => {
    Object.keys(data).forEach(key => {
        if (state.data.unit[key]) {
            state.data.unit[key] = data[key]
        }
    })
}

/**
 * Set the OPEX values
 *
 * @param  {Object} state
 * @param  {Array} data
 * @return {Void}
 */
const INGEST_OPEX = (state, data) => {
    state.data.opex['OperatingExpenseSerializer'] = data['OperatingExpenseSerializer']
    state.data.unit['MiscellaneousIncomePerUnitSerializer'] = data['MiscellaneousIncomePerUnitSerializer']
}

/*
 * Update a uploaded file ID
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_FILE_ID = (state, payload) => {
    state.data.meta[payload.type] = payload.id
}

/**
 * Update the AGR values on all rental income
 *
 * @param  {Object} state
 * @param  {Number} value
 * @return {Void}
 */
const UPDATE_ALL_AGR = (state, value) => {
    state.data.unit.UnitResidentialSerializer = state.data.unit.UnitResidentialSerializer.map(i => {
        i.rental_annual_growth_rate = value
        return i
    })
}

/**
 * Update the AGR values on all rental income
 *
 * @param  {Object} state
 * @param  {Number} value
 * @return {Void}
 */
const UPDATE_ALL_OPEX_AGR = (state, value) => {
    state.data.opex.OperatingExpenseSerializer = state.data.opex.OperatingExpenseSerializer.map(i => {
        i.terminal_agr = value
        return i
    })
}

/**
 * Set the form metadata
 *
 * @param  {Object} state
 * @param  {Object} meta
 * @return {Void}
 */
const SET_FORM_META = (state, meta) => {
    state.meta = meta
}

const REMOVE_CAPEX = (state, payload) => {
    state.data.capex[payload.type] = state.data.capex[payload.type].filter(i => i.id !== payload.id)
}

const SET_CAPEX_LOADING = state => {
    state.capex_loading = true
}

const RESET_CAPEX_LOADING = state => {
    state.capex_loading = false
}

const UPDATE_CAPEX = (state, payload) => {
    let idx = state.data.capex[payload.type].findIndex(i => i.id === payload.id)
    state.data.capex[payload.type].splice(idx, 1, JSON.parse(JSON.stringify(payload.data)))
}

const REPLACE_CAPEX_REPEATER = (state, payload) => {
    let abbr = null
    switch (payload.type) {
        case 'AcquisitionCostSerializer':
            abbr = 'AC'
            break
        case 'HardCostSerializer':
            abbr = 'HC'
            break
        case 'SoftCostSerializer':
            abbr = 'SC'
            break
        default:
            abbr = null
    }
    state.data.capex[payload.type] = payload.data.map(i => {
        i.capex_type = abbr
        return i
    })
}

const ADD_CAPEX = (state, payload) => {
    state.data.capex[payload.type].push(payload.data)
}

export default {
    ADD_CAPEX,
    UPDATE_CAPEX,
    REPLACE_CAPEX_REPEATER,
    SET_CAPEX_LOADING,
    RESET_CAPEX_LOADING,
    RESET_FORM,
    INGEST_OPEX,
    RESET_STATE,
    SAVING_DRAFT,
    PROJECT_SAVE,
    ADD_NEW_DEBT,
    REMOVE_CAPEX,
    SET_FORM_META,
    RESTORE_DRAFT,
    REMOVE_EQUITY,
    ADD_NEW_EQUITY,
    UPDATE_FILE_ID,
    UPDATE_ALL_AGR,
    SET_DEBT_OBJECT,
    CHANGE_FORM_TAB,
    ADD_CAPEX_CLONE,
    SET_PROJECT_FORM,
    GET_PROJECT_FORM,
    REMOVE_ERROR_KEY,
    GET_DRAFT_FAILURE,
    GET_DRAFT_SUCCESS,
    IMPORT_UNIT_VALUES,
    RESET_SAVING_DRAFT,
    RESET_SAVE_SUCCESS,
    UPDATE_ERROR_INDEX,
    REMOVE_CAPEX_ERROR,
    UPDATE_ALL_OPEX_AGR,
    PROJECT_SAVE_SUCCESS,
    PROJECT_SAVE_FAILURE,
    UPDATE_PROJECT_VALUE,
    UPDATE_EXISTING_DEBT,
    UPDATE_RELATIVE_VALUE,
    PROJECT_CHOICES_RESET,
    UPDATE_EXISTING_CAPEX,
    PROJECT_MAKE_SELECTION,
    UPDATE_EXISTING_EQUITY,
    REPLACE_REPEATER_OBJECT,
    GET_PROJECT_FORM_FAILURE,
    SET_EDIT_PAYLOAD,
    SET_WORKING_CAPITAL_MODEL,
    UPDATE_PROJECT_VALUE_FAILED,
    REMOVE_WORKING_CAPITAL_MODEL,
    ADD_REPEATABLE_PROJECT_VALUE,
    REMOVE_DELETED_RELATIVE_VALUE,
    UPDATE_REPEATABLE_OBJECT_VALUE,
    UPDATE_REPEATABLE_PROJECT_VALUE,
    REMOVE_REPEATABLE_PROJECT_VALUE,
    UPDATE_REPEATABLE_PROJECT_VALUE_POSITION
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/form/mutations.js